import React, { useEffect, useState } from 'react';
import {
  Select, MenuItem, Button, InputLabel, TextField,
  FormControlLabel, Checkbox
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { apiGet, apiPutForm } from '../../../../functions/api';
import CurrencyTextField from '@unicef/material-ui-currency-textfield';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const EditarPresupuesto = ({ data, reload, handleClose }) => {
  const departamentos = useSelector(state => state.opciones.areas);
  const auth = useSelector(state => state.authUser.access_token);

  const [tables, setTables] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [comment, setComment] = useState('');
  const [idPresu, setIdPresu] = useState('');
  const [authorizedMonths, setAuthorizedMonths] = useState(months.map(() => false));

  // ===================== CARGA =====================
  useEffect(() => {
    const fetchPresupuesto = async () => {
      try {
        const response = await apiGet(`presupuestosdep/edit/${data.id}`, auth);
        const res = response.data.presupuesto[0];

        setStartDate(res.fecha_inicio);
        setEndDate(res.fecha_fin);
        setComment(res.nombre);
        setIdPresu(res.id);

        const newTables = {};

        res.rel.forEach(item => {
          const idArea = item.id_area;

          if (!newTables[idArea]) {
            newTables[idArea] = { id: idArea, name: idArea, rows: [] };
          }

          const montos = months.map(m => ({
            precio: parseFloat(item[`${m.toLowerCase()}_precio`] || 0),
            cantidad: parseFloat(item[`${m.toLowerCase()}_cantidad`] || 0),
            monto: parseFloat(item[m.toLowerCase()] || 0)
          }));

          newTables[idArea].rows.push({
            id: item.id,
            select1: item.id_partida,
            select2: item.id_subareas,
            montos
          });
        });

        setAuthorizedMonths(months.map(m => res.mes?.[0]?.[m.toLowerCase()] === 1));
        setTables(Object.values(newTables));
      } catch (error) {
        console.error(error);
        Swal.fire('Error', 'No se pudo cargar el presupuesto', 'error');
      }
    };

    fetchPresupuesto();
  }, [data.id, auth]);

  // ===================== FUNCIONES =====================

  const handleMonthAuthorizationChange = (index) => {
    setAuthorizedMonths(prev => {
      const copy = [...prev];
      copy[index] = !copy[index];
      return copy;
    });
  };

  const handleSelectChange = (tableId, rowId, field, value) => {
    setTables(prev =>
      prev.map(table =>
        table.id !== tableId ? table : {
          ...table,
          rows: table.rows.map(row =>
            row.id === rowId ? { ...row, [field]: value } : row
          )
        }
      )
    );
  };

  const handleMontosChange = (tableId, rowId, monthIndex, field, value) => {
    setTables(prev =>
      prev.map(table =>
        table.id !== tableId ? table : {
          ...table,
          rows: table.rows.map(row => {
            if (row.id !== rowId) return row;

            const montos = [...row.montos];
            montos[monthIndex] = { ...montos[monthIndex], [field]: value };

            montos[monthIndex].monto =
              (montos[monthIndex].precio || 0) * (montos[monthIndex].cantidad || 0);

            return { ...row, montos };
          })
        }
      )
    );
  };

  const handleAddRow = (tableId) => {
    setTables(prev =>
      prev.map(table =>
        table.id !== tableId ? table : {
          ...table,
          rows: [
            ...table.rows,
            {
              id: `new-${Date.now()}`,
              select1: '',
              select2: '',
              montos: months.map(() => ({ precio: 0, cantidad: 0, monto: 0 }))
            }
          ]
        }
      )
    );
  };

  const handleDeleteRow = (tableId, rowId) => {
    setTables(prev =>
      prev.map(table =>
        table.id !== tableId ? table : {
          ...table,
          rows: table.rows.filter(row => row.id !== rowId)
        }
      )
    );
  };

  const calculateRowTotal = row =>
    row.montos.reduce((sum, m) => sum + m.monto, 0);

  const calculateDepartmentTotal = table =>
    table.rows.reduce((sum, row) => sum + calculateRowTotal(row), 0);

  const calculateTotalGlobal = () =>
    tables.reduce((sum, table) => sum + calculateDepartmentTotal(table), 0);

  // ===================== GUARDAR =====================

  const handleSendTables = async () => {
    const formattedData = [];

    tables.forEach(table => {
      table.rows.forEach(row => {
        formattedData.push({
          id: typeof row.id === 'number' ? row.id : null,
          departamentoId: table.name,
          partida: row.select1,
          subpartida: row.select2,
          months: row.montos.map((m, i) => ({
            month: months[i],
            monto: m.monto,
            precio: m.precio,
            cantidad: m.cantidad
          }))
        });
      });
    });

    const dataToSend = {
      totalGlobal: calculateTotalGlobal(),
      data: formattedData,
      fecha_inicio: startDate,
      fecha_fin: endDate,
      nombre: comment,
      autorizados: authorizedMonths,
      presuId: idPresu,
      tab: 'departamento'
    };

    try {
      Swal.fire({ title: 'Guardando...', allowOutsideClick: false });
      Swal.showLoading();
      await apiPutForm(`presupuestosdep/update/${idPresu}`, dataToSend, auth);
      Swal.close();
      Swal.fire('Guardado', 'Presupuesto editado con éxito', 'success');
      reload?.reload();
      handleClose();
    } catch (err) {
      Swal.fire('Error', 'Error al guardar', 'error');
    }
  };

  // ===================== UI =====================

  return (
    <div className="container">

      <div className="row mb-3">
        <div className="col-md-3">
          <InputLabel>Fecha inicio</InputLabel>
          <TextField type="date" fullWidth value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="col-md-3">
          <InputLabel>Fecha fin</InputLabel>
          <TextField type="date" fullWidth value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <div className="col-md-4">
          <TextField label="Nombre presupuesto" value={comment} onChange={e => setComment(e.target.value)} fullWidth />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <Button variant="contained" color="primary" onClick={handleSendTables} fullWidth>
            Guardar
          </Button>
        </div>
      </div>

      <h4>Total Global: {calculateTotalGlobal().toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}</h4>

      {tables.map(table => {
        const departamento = departamentos.find(d => String(d.id_area) === String(table.name));
        const partidas = departamento?.partidas || [];

        return (
          <div key={table.id} className="mt-4">
            <h5>{departamento?.nombreArea}</h5>

            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Partida</th>
                  <th>Subpartida</th>
                  {months.map((m, i) => (
                    <th key={i}>
                      {m}
                      <br />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={authorizedMonths[i]}
                            onChange={() => handleMonthAuthorizationChange(i)}
                            color="primary"
                          />
                        }
                        label="Autorizar"
                      />
                    </th>
                  ))}
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {table.rows.map(row => {
                  const partida = partidas.find(p => String(p.id) === String(row.select1));
                  const subpartidas = partida?.subpartidas || [];

                  return (
                    <tr key={row.id}>
                      <td>
                        <Select fullWidth value={row.select1}
                          onChange={e => handleSelectChange(table.id, row.id, 'select1', e.target.value)}>
                          {partidas.map(p => (
                            <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                          ))}
                        </Select>
                      </td>

                      <td>
                        <Select fullWidth value={row.select2}
                          onChange={e => handleSelectChange(table.id, row.id, 'select2', e.target.value)}>
                          {subpartidas.map(s => (
                            <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                          ))}
                        </Select>
                      </td>

                      {row.montos.map((m, i) => (
                        <td key={i}>
                          <CurrencyTextField
                            value={m.precio}
                            currencySymbol="$"
                            outputFormat="number"
                            decimalPlaces={2}
                            onChange={(e, v) => handleMontosChange(table.id, row.id, i, 'precio', v)}
                            style={{ width: 80 }}
                          />
                          <CurrencyTextField
                            value={m.cantidad}
                            currencySymbol=""
                            outputFormat="number"
                            decimalPlaces={2}
                            onChange={(e, v) => handleMontosChange(table.id, row.id, i, 'cantidad', v)}
                            style={{ width: 80 }}
                          />
                          <div>{m.monto.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}</div>
                        </td>
                      ))}

                      <td>{calculateRowTotal(row).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}</td>

                      <td>
                        <Button onClick={() => handleDeleteRow(table.id, row.id)} color="secondary">
                          <DeleteIcon />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Button startIcon={<AddIcon />} onClick={() => handleAddRow(table.id)}>
              Agregar partida
            </Button>

            <div className="mt-2">
              <strong>Subtotal:</strong> {calculateDepartmentTotal(table).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EditarPresupuesto;
